import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	IconButton,
	MenuItem,
	Select,
	Tooltip,
} from "@mui/material";
import Icon from "@mdi/react";
import {
	mdiBookOpenPageVariant,
	mdiArchiveOutline,
	mdiCheckCircleOutline,
	mdiDelete,
	mdiDownload,
	mdiUpload,
	mdiEye,
	mdiClockOutline,
} from "@mdi/js";
import {
	KnowledgeBaseBackofficeServices,
	KbWorkspaceStats,
	PublicKbArticleDTO,
	NotistackServices,
} from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

/**
 * EK-424 — Backoffice global da KB.
 * Lista todos workspaces com stats e permite gerir artigos de qualquer um.
 * Acesso restrito a MANAGER+ (validado no backend via AuthMiddleware.containsPermissionByLevel).
 */
export const KnowledgeBaseBackofficePage: React.FC = () => {
	const [workspaces, setWorkspaces] = useState<KbWorkspaceStats[]>([]);
	const [selectedWs, setSelectedWs] = useState<KbWorkspaceStats | null>(null);
	const [loading, setLoading] = useState(true);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			const ws = await KnowledgeBaseBackofficeServices.listWorkspacesStats();
			setWorkspaces(ws);
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro ao listar KB");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { void refresh(); }, [refresh]);

	if (loading) return <div className={styles.loading}><CircularProgress /></div>;

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<h1 className={styles.title}>
					<Icon path={mdiBookOpenPageVariant} size={1} /> Knowledge Base (Global)
				</h1>
				<p className={styles.subtitle}>{workspaces.length} workspaces com KB</p>
			</div>

			<div className={styles.grid}>
				<aside className={styles.list}>
					{workspaces.length === 0 ? (
						<p className={styles.emptyText}>Nenhum workspace com KB ainda.</p>
					) : workspaces.map((w) => (
						<div
							key={w.workspaceId}
							className={`${styles.workspaceItem} ${selectedWs?.workspaceId === w.workspaceId ? styles.active : ""}`}
							onClick={() => setSelectedWs(w)}
						>
							<h4>{w.workspaceName}</h4>
							<div className={styles.statsRow}>
								<span>{w.articlesCount} artigos</span>
								<span>•</span>
								<span>{w.publishedCount} pub.</span>
								<span>•</span>
								<span>{w.totalViews} views</span>
							</div>
							{w.lastPublishedAt && (
								<div className={styles.lastUpdate}>
									<Icon path={mdiClockOutline} size={0.5} /> {new Date(w.lastPublishedAt).toLocaleDateString("pt-BR")}
								</div>
							)}
						</div>
					))}
				</aside>

				<main className={styles.detail}>
					{selectedWs ? (
						<WorkspaceKbDetail workspace={selectedWs} onAfterAction={refresh} />
					) : (
						<div className={styles.placeholder}>
							<Icon path={mdiBookOpenPageVariant} size={2} />
							<p>Selecione um workspace na esquerda p/ gerir sua KB.</p>
						</div>
					)}
				</main>
			</div>
		</div>
	);
};

// ── Detalhe de um workspace selecionado ────────────────────────────
const WorkspaceKbDetail: React.FC<{
	workspace: KbWorkspaceStats;
	onAfterAction: () => void;
}> = ({ workspace, onAfterAction }) => {
	const [articles, setArticles] = useState<PublicKbArticleDTO[]>([]);
	const [statusFilter, setStatusFilter] = useState<"all" | "draft" | "published" | "archived">("all");
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			const r = await KnowledgeBaseBackofficeServices.listArticles(workspace.workspaceId, {
				status: statusFilter === "all" ? undefined : statusFilter,
				limit: 200,
			});
			setArticles(r.items);
			setSelected(new Set());
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro ao listar artigos");
		} finally {
			setLoading(false);
		}
	}, [workspace.workspaceId, statusFilter]);

	useEffect(() => { void refresh(); }, [refresh]);

	const filtered = useMemo(() => articles, [articles]);
	const allSelected = filtered.length > 0 && filtered.every((a) => selected.has(a.id));

	const bulk = async (action: "publish" | "archive" | "delete") => {
		if (selected.size === 0) return;
		if (action === "delete" && !window.confirm(`Excluir ${selected.size} artigos do workspace ${workspace.workspaceName}?`)) return;
		setBusy(true);
		try {
			const r = await KnowledgeBaseBackofficeServices.bulkArticleAction(workspace.workspaceId, Array.from(selected), action);
			NotistackServices.showSuccessMessage(`${r.affected} artigos afetados`);
			refresh();
			onAfterAction();
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro no bulk");
		} finally {
			setBusy(false);
		}
	};

	const exportKB = async () => {
		setBusy(true);
		try {
			const data = await KnowledgeBaseBackofficeServices.exportWorkspace(workspace.workspaceId);
			const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `kb-export-${workspace.workspaceName.replace(/\W+/g, "_")}-${new Date().toISOString().slice(0, 10)}.json`;
			link.click();
			URL.revokeObjectURL(url);
			NotistackServices.showSuccessMessage(`Exportado: ${data.articles.length} artigos`);
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro no export");
		} finally {
			setBusy(false);
		}
	};

	const importKB = async (file: File, mode: "merge" | "replace") => {
		setBusy(true);
		try {
			const text = await file.text();
			const data = JSON.parse(text);
			const r = await KnowledgeBaseBackofficeServices.importWorkspace(workspace.workspaceId, data, mode);
			NotistackServices.showSuccessMessage(`Import: ${r.articles} artigos, ${r.skipped} ignorados`);
			refresh();
			onAfterAction();
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro no import");
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className={styles.detailInner}>
			<div className={styles.detailHeader}>
				<div>
					<h2>{workspace.workspaceName}</h2>
					<p className={styles.subtitle}>
						{workspace.categoriesCount} cats • {workspace.tagsCount} tags • {workspace.articlesCount} artigos ({workspace.publishedCount} pub.)
					</p>
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					<input
						ref={fileInputRef}
						type="file"
						accept="application/json"
						style={{ display: "none" }}
						onChange={async (e) => {
							const f = e.target.files?.[0];
							if (!f) return;
							const mode = window.confirm("Modo REPLACE? OK = replace (apaga tudo), Cancel = merge") ? "replace" : "merge";
							await importKB(f, mode);
							e.target.value = "";
						}}
					/>
					<Button size="small" variant="outlined" disabled={busy} onClick={() => fileInputRef.current?.click()} startIcon={<Icon path={mdiUpload} size={0.7} />}>Import</Button>
					<Button size="small" variant="outlined" disabled={busy} onClick={exportKB} startIcon={<Icon path={mdiDownload} size={0.7} />}>Export</Button>
				</div>
			</div>

			<div className={styles.toolbar}>
				<Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
					<MenuItem value="all">Todos</MenuItem>
					<MenuItem value="draft">Draft</MenuItem>
					<MenuItem value="published">Published</MenuItem>
					<MenuItem value="archived">Archived</MenuItem>
				</Select>
				{selected.size > 0 && (
					<>
						<span style={{ fontSize: "0.85rem", color: "var(--mui-palette-text-secondary)" }}>{selected.size} selecionados</span>
						<Button size="small" variant="outlined" color="success" disabled={busy} onClick={() => bulk("publish")} startIcon={<Icon path={mdiCheckCircleOutline} size={0.7} />}>Publicar</Button>
						<Button size="small" variant="outlined" disabled={busy} onClick={() => bulk("archive")} startIcon={<Icon path={mdiArchiveOutline} size={0.7} />}>Arquivar</Button>
						<Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => bulk("delete")} startIcon={<Icon path={mdiDelete} size={0.7} />}>Excluir</Button>
					</>
				)}
			</div>

			{loading ? (
				<div className={styles.loading}><CircularProgress /></div>
			) : (
				<table className={styles.table}>
					<thead>
						<tr>
							<th style={{ width: 36 }}>
								<Checkbox size="small" checked={allSelected} indeterminate={selected.size > 0 && !allSelected}
									onChange={() => setSelected(allSelected ? new Set() : new Set(filtered.map((a) => a.id)))} />
							</th>
							<th>Título</th>
							<th>Status</th>
							<th>Categoria</th>
							<th>Views</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{filtered.map((a) => (
							<tr key={a.id}>
								<td><Checkbox size="small" checked={selected.has(a.id)} onChange={() => {
									const next = new Set(selected);
									if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
									setSelected(next);
								}} /></td>
								<td>{a.title}</td>
								<td>
									<Chip size="small" label={a.status} color={a.status === "published" ? "success" : a.status === "draft" ? "warning" : "default"} variant="outlined" />
								</td>
								<td>{a.category?.name ?? "—"}</td>
								<td>{a.viewCount}</td>
								<td>
									<Tooltip title="Excluir">
										<IconButton size="small" onClick={async () => {
											if (!window.confirm(`Excluir "${a.title}"?`)) return;
											await KnowledgeBaseBackofficeServices.deleteArticle(workspace.workspaceId, a.id);
											refresh();
											onAfterAction();
										}}>
											<Icon path={mdiDelete} size={0.7} />
										</IconButton>
									</Tooltip>
								</td>
							</tr>
						))}
						{filtered.length === 0 && (
							<tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "var(--mui-palette-text-secondary)" }}>Nenhum artigo</td></tr>
						)}
					</tbody>
				</table>
			)}
		</div>
	);
};
